import React from 'react';
import {
  List,
  TopToolbar,
  useListContext,
  useRedirect,
} from 'react-admin';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
} from '@mui/material';
import {
  Business as BusinessIcon,
} from '@mui/icons-material';
import { CustomEmptyState } from '../../components/CustomEmptyState';
import { PageTitle } from '../../components/PageTitle';

const ListActions = () => {
  return <TopToolbar />;
};

const EnterpriseCard = ({ record, onClick }: { record: any; onClick: () => void }) => {
  return (
    <Card
      sx={{
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: 2,
        },
        borderLeft: `4px solid #1976d2`,
        borderRadius: 0,
        minHeight: 'auto',
        mb: 1,
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: '400px 1fr 200px',
          gap: 2,
          alignItems: 'center',
          width: '100%'
        }}>
          {/* Column 1: Avatar and Name */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              src={record.logoUrl}
              sx={{
                bgcolor: '#1976d2',
                width: 48,
                height: 48
              }}
            >
              <BusinessIcon sx={{ fontSize: 24 }} />
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="subtitle1"
                component="h3"
                sx={{
                  fontWeight: 600,
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {record.name}
              </Typography>
              {record.description && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    fontSize: '0.85rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {record.description}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Column 2: Stats */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {record._count && (
              <>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Brands
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {record._count.brands || 0}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Franchisees
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {record._count.tenants || 0}
                  </Typography>
                </Box>
              </>
            )}
          </Box>

          {/* Column 3: Status */}
          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
            <Chip
              label="Active"
              size="small"
              sx={{
                bgcolor: '#4CAF50',
                color: 'white',
                fontWeight: 500,
                height: 24,
                fontSize: '0.7rem'
              }}
            />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const EnterpriseListHeader = () => (
  <Box sx={{
    display: 'grid',
    gridTemplateColumns: '400px 1fr 200px',
    gap: 2,
    alignItems: 'center',
    px: 2,
    py: 1.5,
    backgroundColor: 'grey.50',
    borderRadius: 0,
    mb: 2,
    border: '1px solid',
    borderColor: 'grey.200'
  }}>
    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
      Enterprise Name
    </Typography>
    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
      Statistics
    </Typography>
    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', textAlign: 'right' }}>
      Status
    </Typography>
  </Box>
);

const EnterpriseCardList = () => {
  const { data, isLoading } = useListContext();
  const redirect = useRedirect();

  if (isLoading) {
    return <Box>Loading...</Box>;
  }

  if (!data || data.length === 0) {
    return null;
  }

  return (
    <Box>
      <EnterpriseListHeader />
      {data.map((record: any) => (
        <EnterpriseCard
          key={record.id}
          record={record}
          onClick={() => redirect(`/enterprises/${record.id}/show`)}
        />
      ))}
    </Box>
  );
};

export const EnterpriseList = () => {
  return (
    <Box>
      <PageTitle
        title="Enterprises"
        titleKey="pages.enterprises"
        subtitle="Manage franchise enterprises"
        icon={<BusinessIcon />}
      />
      <List
        actions={<ListActions />}
        perPage={25}
        sort={{ field: 'name', order: 'ASC' }}
        empty={<CustomEmptyState resource="enterprises" />}
        sx={{
          '& .RaList-main': { mt: 0 },
          '& .RaList-content': { p: 2 }
        }}
      >
        <EnterpriseCardList />
      </List>
    </Box>
  );
};
